/** Display formatting shared by every customer page. UK English, AU racing conventions. */

/** $2.30, or an en dash when unknown. */
export const money = (n: number | null | undefined): string =>
  n == null || Number.isNaN(Number(n)) ? '–' : `$${Number(n).toFixed(2)}`;

/** "$6.00 / $2.45": the starting price, then the place price when we have one. */
export const spPlace = (sp: number | null | undefined, place?: number | null): string =>
  Number(place) > 0 ? `${money(sp)} / ${money(place)}` : money(sp);

/** 26.5% (one decimal by default), or an en dash when unknown. */
export const pct = (n: number | null | undefined, decimals = 1): string =>
  n == null || Number.isNaN(Number(n)) ? '–' : `${Number(n).toFixed(decimals)}%`;

/** Signed ROI with an up/down marker: "▲ +29.3%" / "▼ −34.3%" / "0.0%". */
export const roiText = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(Number(n))) return '–';
  const v = Number(n);
  if (v > 0) return `▲ +${v.toFixed(1)}%`;
  if (v < 0) return `▼ −${Math.abs(v).toFixed(1)}%`;
  return '0.0%';
};

export const roiDirection = (n: number | null | undefined): 'up' | 'down' | 'flat' =>
  n == null || Number(n) === 0 ? 'flat' : Number(n) > 0 ? 'up' : 'down';

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th, 21st. */
export const ordinal = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

/** "13:33" or "23-Sep-2026 13:33" → "1:33pm". Returns the input when it has no time. */
export const clockTime = (t: string | null | undefined): string => {
  const m = /(\d{1,2}):(\d{2})/.exec(t ?? '');
  if (!m) return t ?? '';
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]}${h < 12 ? 'am' : 'pm'}`;
};

/**
 * An ISO instant shown as the viewer's own clock time, e.g. "12:32pm", so it
 * agrees with the countdown beside it whichever state (and DST rule) they are in.
 * `timeZone` is for tests; leave it out in the UI.
 */
export const localTime = (iso: string, timeZone?: string): string =>
  new Date(iso)
    .toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone })
    .replace(/\s/g, '')
    .toLowerCase();

/** Minutes → "Now" / "12m" / "1h 5m". */
export const countdown = (minutes: number): string => {
  if (minutes <= 0) return 'Now';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

/** "2026-09-23" → "Wed, 23 Sept". */
export const shortDay = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

/** "2026-09-23" → "Wednesday 23 September 2026". */
export const longDay = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/** Title-case a word from the data ("metropolitan" → "Metropolitan"). */
export const titleCase = (s: string | null | undefined): string =>
  (s ?? '').toLowerCase().replace(/(^|[\s-])(\w)/g, (_m, sep: string, c: string) => sep + c.toUpperCase());

/** "1 winner" / "6 winners". */
export const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The race header clock, as the site has always shown it: the start time, a
 * "4m 12s" countdown inside five minutes, "-20s" for 45 seconds after the
 * jump, then "Closed" until the result lands.
 */
export const raceClock = (startIso: string, now: number): { text: string; imminent: boolean } => {
  const diff = Date.parse(startIso) - now;
  if (diff > 300000) return { text: localTime(startIso), imminent: false };
  if (diff > 0) {
    const s = Math.floor(diff / 1000);
    return { text: `${Math.floor(s / 60)}m ${s % 60}s`, imminent: true };
  }
  if (diff >= -45000) return { text: `-${Math.floor(-diff / 1000)}s`, imminent: true };
  return { text: 'Closed', imminent: false };
};

/**
 * The upcoming strip's clock, as the classic site had it: "12m" / "1h 5m",
 * then "4m 12s" inside five minutes and "Now" once the race is due (the strip
 * drops it 45 seconds after the jump). `urgent` turns the row red.
 */
export const upcomingClock = (startIso: string, now: number): { text: string; urgent: boolean } => {
  const diff = Date.parse(startIso) - now;
  if (diff <= 0) return { text: 'Now', urgent: true };
  if (diff < 300000) {
    const s = Math.floor(diff / 1000);
    const m = Math.floor(s / 60);
    return { text: m ? `${m}m ${s % 60}s` : `${s}s`, urgent: true };
  }
  return { text: countdown(Math.round(diff / 60000)), urgent: false };
};
